"use client";

import { buildDiscoverProductsRequest } from "@faire/retailer-shared/search/searchRequest";
import fetchSearchPageBanners from "@faire/web-api/api/v2/products/search/banners/post";
import { route as discoverRoute } from "@faire/web-api/api/v2/search/products/discover/get";
import { ISearchCatNavBannersRequest } from "@faire/web-api/indigofair/data/ISearchCatNavBannersRequest";
import { PageType } from "@faire/web-api/indigofair/data/PageType";
import { useLocalizationStore } from "@faire/web/common/localization/LocalizationStore";
import { wrapAsyncFn } from "@faire/web/common/wrapAsyncFn";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import React, { PropsWithChildren, ReactNode, useMemo } from "react";

import { useSearchPageBannersResponse } from "@faire/retailer/components/Banners/DataFetching/useSearchPageBannersData";
import { useRawBannerData } from "@faire/retailer/components/Banners/SearchBanners/RawBannerDataContextProvider";
import { PageLoadEvent } from "@faire/retailer/services/performance/const";
import { PerformanceMarker } from "@faire/retailer/services/performance/PerformanceMarker";
import { useProductCacheStore } from "@faire/retailer/stores/domain/ProductCacheStore";
import {
  SearchPageBannersDataContextProvider,
  useSearchPageBannersDataContext,
} from "@faire/retailer/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/ViewState/SearchPageBannersDataContext";
import { SearchPageDataContextProvider } from "@faire/retailer/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/ViewState/SearchPageDataContext";
import {
  getPageSize,
  getShouldWaitForBannersQuery,
} from "@faire/retailer/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/ViewState/searchPageDataUtil";
import { SearchPageFiltersDataContextProvider } from "@faire/retailer/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/ViewState/SearchPageFiltersDataContext";
import { useSearchPageFilters } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useSearchPageFilters";
import { useSearchPageParsedSearch } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useSearchPageParsedSearch";
import { useSearchPageProductSearchString } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";
import { useSearchPageZeroIndexedPageNumber } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useSearchPageZeroIndexedPageNumber";

const STALE_TIME_MS = 5 * 60 * 1000;

export const DiscoverPageWithUseQueryContextProvider: React.FC<
  PropsWithChildren
> = ({ children }) => {
  return (
    <DiscoverPageBannersUseQueryDataProvider>
      <DiscoverPageDataProvider>{children}</DiscoverPageDataProvider>
    </DiscoverPageBannersUseQueryDataProvider>
  );
};

export const useDiscoverPageQueryQueryKey = () => {
  const { locale } = useLocalizationStore(["locale"]);
  const query = useSearchPageProductSearchString();
  const filters = useSearchPageFilters();
  const pageNumber = useSearchPageZeroIndexedPageNumber();

  return useMemo(
    () => [discoverRoute, query, filters, pageNumber, locale],
    [query, filters, pageNumber, locale]
  );
};

export const DiscoverPageBannersUseQueryDataProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const query = useSearchPageProductSearchString();
  const filters = useSearchPageFilters();

  /**
   * In RSC, we fetch the banners data at the server and send it to the client
   * side via RawBannerDataContext. If the data is available, we use it directly
   * instead of fetching it again.
   */
  const serverData = useRawBannerData();

  const request = ISearchCatNavBannersRequest.build({
    query: query,
    filter_keys: filters,
    page_type: PageType.DISCOVER_PAGE,
  });

  // if the serverData is not available, we fetch it again
  const { data: clientData } = useQuery({
    queryKey: [request],
    queryFn: () => fetchSearchPageBanners(request),
    enabled: !!query && !serverData,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });

  const {
    bannersResponse,
    bannersDisplayData,
    replacedProductTilesCount,
    displayableBannersWithAdjustedRow,
  } = useSearchPageBannersResponse(serverData ?? clientData);

  const data = useMemo(
    () => ({
      bannersResponse,
      bannersDisplayData,
      replacedProductTilesCount,
      displayableBannersWithAdjustedRow,
    }),
    [
      bannersResponse,
      bannersDisplayData,
      replacedProductTilesCount,
      displayableBannersWithAdjustedRow,
    ]
  );

  return (
    <SearchPageBannersDataContextProvider searchBannersData={data}>
      {children}
    </SearchPageBannersDataContextProvider>
  );
};

const DiscoverPageDataProvider = ({ children }: { children: ReactNode }) => {
  const zeroIndexedPageNumber = useSearchPageZeroIndexedPageNumber();

  const { getDiscoverPageLeanProductTiles } = useProductCacheStore([
    "getDiscoverPageLeanProductTiles",
  ]);
  const queryKey = useDiscoverPageQueryQueryKey();
  const { locale } = useLocalizationStore(["locale"]);
  const parsedSearch = useSearchPageParsedSearch();
  const searchBannersData = useSearchPageBannersDataContext();
  const pageSize = getPageSize(searchBannersData.replacedProductTilesCount);
  const shouldWaitForBannersQuery = getShouldWaitForBannersQuery(
    zeroIndexedPageNumber,
    !searchBannersData.bannersResponse
  );
  const productsAndFiltersQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      wrapAsyncFn(
        () =>
          getDiscoverPageLeanProductTiles(
            buildDiscoverProductsRequest({
              parsedSearch: parsedSearch,
              locale: locale,
              pageSize: pageSize,
            }),
            signal
          ),
        {
          after: () => {
            PerformanceMarker.get().mark(PageLoadEvent.primaryFetchEnd);
          },
        }
      ),
    enabled: !shouldWaitForBannersQuery,
    staleTime: STALE_TIME_MS,
  });

  const productsData = useMemo(() => {
    return {
      productsResponse: productsAndFiltersQuery.data,
      loading: productsAndFiltersQuery.isFetching,
      isError: productsAndFiltersQuery.isError,
      error: productsAndFiltersQuery.error,
      dataUpdatedAt: productsAndFiltersQuery.dataUpdatedAt,
      rscPageLoadKey: "", // noOp
    };
  }, [
    productsAndFiltersQuery.data,
    productsAndFiltersQuery.isFetching,
    productsAndFiltersQuery.isError,
    productsAndFiltersQuery.error,
    productsAndFiltersQuery.dataUpdatedAt,
  ]);

  const filtersData = useMemo(() => {
    return {
      filtersResponse: productsAndFiltersQuery.data,
      loading: productsAndFiltersQuery.isFetching,
      isError: productsAndFiltersQuery.isError,
      error: productsAndFiltersQuery.error,
    };
  }, [
    productsAndFiltersQuery.data,
    productsAndFiltersQuery.isFetching,
    productsAndFiltersQuery.isError,
    productsAndFiltersQuery.error,
  ]);

  return (
    <SearchPageDataContextProvider searchPageData={productsData}>
      <SearchPageFiltersDataContextProvider searchPageFiltersData={filtersData}>
        {children}
      </SearchPageFiltersDataContextProvider>
    </SearchPageDataContextProvider>
  );
};
